import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './CollectionAutomationPage.css';

const defaultRowSettings = {
  enabled: false,
  reorderOnly: false,
  maxAddPerRun: 10,
  maxRemovePerRun: 10,
  minCollectionSize: 0,
  maxCollectionSize: 0,
  cooldownTopX: 0,
  cooldownScope: 'per_collection',
  allowedProductTypes: [],
  runMode: 'dry_run',
  schedule: {
    frequency: 'weekly',
    dayOfWeek: 'MONDAY',
    time: '03:00',
    timeZone: 'Asia/Kolkata',
  },
};

const APPLY_ALERT_STALE_DAYS = 8;
const DAY_MS = 24 * 60 * 60 * 1000;

function toMillisSafe(value) {
  if (!value) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'object') {
    const seconds = value.seconds ?? value._seconds;
    const nanos = value.nanoseconds ?? value._nanoseconds ?? 0;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      return (seconds * 1000) + Math.floor(Number(nanos || 0) / 1e6);
    }
  }
  return null;
}

function formatDateTime(value) {
  const ms = toMillisSafe(value);
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleString();
  } catch (_) {
    return '—';
  }
}

function formatRelative(value) {
  const ms = toMillisSafe(value);
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (!Number.isFinite(diff) || diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  const days = Math.floor(diff / DAY_MS);
  return `${days}d ago`;
}

function normalizeRowForDraft(collection = {}) {
  const settings = collection?.settings || {};
  const schedule = settings?.schedule || {};
  return {
    isTargeted: collection?.isTargeted === true,
    settings: {
      ...defaultRowSettings,
      ...settings,
      schedule: {
        ...defaultRowSettings.schedule,
        ...schedule,
      },
    },
  };
}

function CollectionAutomationPage() {
  const { currentUser } = useAuth();
  const [collections, setCollections] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busySync, setBusySync] = useState(false);
  const [busySnapshot, setBusySnapshot] = useState(false);
  const [busyApplyAll, setBusyApplyAll] = useState(false);
  const [busyRunAllReorder, setBusyRunAllReorder] = useState(false);
  const [busyCalcAllReorder, setBusyCalcAllReorder] = useState(false);
  const [savingById, setSavingById] = useState({});
  const [applyingById, setApplyingById] = useState({});
  const [scoringById, setScoringById] = useState({});
  const [snapshotById, setSnapshotById] = useState({});
  const [search, setSearch] = useState('');
  const [manualOnly, setManualOnly] = useState(true);
  const [targetedOnly, setTargetedOnly] = useState(false);
  const [reorderOnlyOnly, setReorderOnlyOnly] = useState(false);
  const [message, setMessage] = useState('');
  const [showGlobalSettings, setShowGlobalSettings] = useState(false);
  const [busyTogglingMasterPause, setBusyTogglingMasterPause] = useState(false);
  const [globalSettings, setGlobalSettings] = useState({
    pauseAllExceptNewArrivals: true,
    snapshotRetentionWeeks: 4,
    scoringWeights: {
      salesMomentum: 55,
      ga4Demand: 35,
      inventory: 10,
    },
    lookbacks: {
      salesDays: 90,
      ga4Days: 90,
      seasonalityMode: 'all_time',
      seasonalityWindowDays: 45,
    },
    defaultSchedule: {
      frequency: 'weekly',
      intervalDays: 7,
      dayOfWeek: 'MONDAY',
      time: '03:00',
      timeZone: 'Asia/Kolkata',
    },
  });
  const [savingGlobalSettings, setSavingGlobalSettings] = useState(false);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffData, setDiffData] = useState({ collectionTitle: '', runId: '', weekKey: '', summary: {}, rows: [] });
  const [diffSort, setDiffSort] = useState({ key: 'changeType', direction: 'asc' });
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkData, setBulkData] = useState({ message: '', total: 0, okCount: 0, failCount: 0, results: [] });

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('User not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = (base && typeof url === 'string' && url.startsWith('/')) ? `${base}${url}` : url;
    const headers = {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };
    return fetch(resolvedUrl, { ...options, headers });
  }, [currentUser]);

  const loadCollections = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authedFetch('/api/v1/collection-automation/collections');
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load collections.');
      }
      const list = Array.isArray(data?.collections) ? data.collections : [];
      setCollections(list);
      const nextDrafts = {};
      list.forEach((collection) => {
        nextDrafts[collection.id] = normalizeRowForDraft(collection);
      });
      setDrafts(nextDrafts);
    } catch (err) {
      setError(err.message || 'Failed to load collections.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  const loadGlobalSettings = useCallback(async () => {
    try {
      const response = await authedFetch('/api/v1/collection-automation/settings');
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load global settings.');
      }
      const incoming = data?.settings || {};
      setGlobalSettings({
        pauseAllExceptNewArrivals: incoming.pauseAllExceptNewArrivals !== undefined ? Boolean(incoming.pauseAllExceptNewArrivals) : true,
        snapshotRetentionWeeks: Number(incoming.snapshotRetentionWeeks ?? 4) || 4,
        scoringWeights: {
          salesMomentum: Number(incoming?.scoringWeights?.salesMomentum ?? 55) || 55,
          ga4Demand: Number(incoming?.scoringWeights?.ga4Demand ?? 35) || 35,
          inventory: Number(incoming?.scoringWeights?.inventory ?? 10) || 10,
        },
        lookbacks: {
          salesDays: Number(incoming?.lookbacks?.salesDays ?? 90) || 90,
          ga4Days: Number(incoming?.lookbacks?.ga4Days ?? 90) || 90,
          seasonalityMode: String(incoming?.lookbacks?.seasonalityMode || 'all_time') || 'all_time',
          seasonalityWindowDays: Number(incoming?.lookbacks?.seasonalityWindowDays ?? 45) || 45,
        },
        defaultSchedule: {
          frequency: String(incoming?.defaultSchedule?.frequency || 'weekly'),
          intervalDays: Number(incoming?.defaultSchedule?.intervalDays ?? 7) || 7,
          dayOfWeek: String(incoming?.defaultSchedule?.dayOfWeek || 'MONDAY'),
          time: String(incoming?.defaultSchedule?.time || '03:00'),
          timeZone: String(incoming?.defaultSchedule?.timeZone || 'Asia/Kolkata'),
        },
      });
    } catch (_) {
      setGlobalSettings({
        pauseAllExceptNewArrivals: true,
        snapshotRetentionWeeks: 4,
        scoringWeights: {
          salesMomentum: 55,
          ga4Demand: 35,
          inventory: 10,
        },
        lookbacks: {
          salesDays: 90,
          ga4Days: 90,
          seasonalityMode: 'all_time',
          seasonalityWindowDays: 45,
        },
        defaultSchedule: {
          frequency: 'weekly',
          intervalDays: 7,
          dayOfWeek: 'MONDAY',
          time: '03:00',
          timeZone: 'Asia/Kolkata',
        },
      });
    }
  }, [authedFetch]);

  const loadProductTypes = useCallback(async () => {
    try {
      const response = await authedFetch('/api/v1/products/types');
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load product types.');
      }
      setProductTypes(Array.isArray(data?.productTypes) ? data.productTypes : []);
    } catch (_) {
      setProductTypes([]);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadCollections();
    loadProductTypes();
    loadGlobalSettings();
  }, [loadCollections, loadProductTypes, loadGlobalSettings]);

  const filteredCollections = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    return collections.filter((collection) => {
      if (manualOnly && collection?.isManual !== true) return false;
      const draft = drafts[collection.id] || normalizeRowForDraft(collection);
      if (targetedOnly && draft.isTargeted !== true) return false;
      if (reorderOnlyOnly && draft?.settings?.reorderOnly !== true) return false;
      if (!q) return true;
      const haystack = `${collection?.title || ''} ${collection?.handle || ''} ${collection?.legacyCollectionId || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [collections, drafts, manualOnly, reorderOnlyOnly, search, targetedOnly]);

  const resolveCount = useCallback((collection) => {
    if (collection?.effectiveCount !== null && collection?.effectiveCount !== undefined) {
      const effective = Number(collection.effectiveCount);
      if (Number.isFinite(effective)) return effective;
    }
    if (collection?.latestMembershipCount !== null && collection?.latestMembershipCount !== undefined) {
      const latest = Number(collection.latestMembershipCount);
      if (Number.isFinite(latest)) return latest;
    }
    if (collection?.productsCount !== null && collection?.productsCount !== undefined) {
      const fallback = Number(collection.productsCount);
      if (Number.isFinite(fallback)) return fallback;
    }
    return null;
  }, []);

  const updateDraft = useCallback((id, updater) => {
    setDrafts((prev) => {
      const current = prev[id] || {};
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, [id]: next };
    });
  }, []);

  const setSettingField = useCallback((id, field, value) => {
    updateDraft(id, (current) => {
      const base = current?.settings ? current : normalizeRowForDraft({});
      return {
        ...base,
        settings: {
          ...defaultRowSettings,
          ...(base.settings || {}),
          [field]: value,
        },
      };
    });
  }, [updateDraft]);

  const setAllowedProductTypes = useCallback((id, values) => {
    const cleaned = Array.from(new Set((Array.isArray(values) ? values : [])
      .map((v) => String(v || '').trim())
      .filter(Boolean)));
    setSettingField(id, 'allowedProductTypes', cleaned);
  }, [setSettingField]);

  const saveRow = useCallback(async (collectionId) => {
    const rowDraft = drafts[collectionId];
    if (!rowDraft) return;
    setSavingById((prev) => ({ ...prev, [collectionId]: true }));
    setError('');
    setMessage('');
    try {
      const response = await authedFetch(`/api/v1/collection-automation/collections/${encodeURIComponent(collectionId)}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          isTargeted: rowDraft.isTargeted === true,
          settings: rowDraft.settings,
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to save row.');
      }
      const updated = data?.collection || null;
      if (updated) {
        setCollections((prev) => prev.map((row) => (row.id === collectionId ? updated : row)));
        setDrafts((prev) => ({ ...prev, [collectionId]: normalizeRowForDraft(updated) }));
      }
      setMessage(`Saved settings for ${updated?.title || collectionId}.`);
    } catch (err) {
      setError(err.message || 'Failed to save settings.');
    } finally {
      setSavingById((prev) => ({ ...prev, [collectionId]: false }));
    }
  }, [authedFetch, drafts]);

  const runSyncCollections = useCallback(async () => {
    setBusySync(true);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch('/api/v1/collection-automation/sync-collections', { method: 'POST' });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to sync collections.');
      }
      setMessage(data?.message || 'Collections synced.');
      await loadCollections();
    } catch (err) {
      setError(err.message || 'Failed to sync collections.');
    } finally {
      setBusySync(false);
    }
  }, [authedFetch, loadCollections]);

  const runSnapshot = useCallback(async () => {
    setBusySnapshot(true);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch('/api/v1/collection-automation/snapshot-membership', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to snapshot membership.');
      }
      setMessage(data?.message || 'Membership snapshot created.');
    } catch (err) {
      setError(err.message || 'Failed to snapshot membership.');
    } finally {
      setBusySnapshot(false);
    }
  }, [authedFetch]);

  const handleTogglePauseAllExceptNewArrivals = useCallback(async (e) => {
    const nextPaused = e.target.checked;
    setBusyTogglingMasterPause(true);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch('/api/v1/collection-automation/toggle-pause-all-except-new-arrivals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pauseAllExceptNewArrivals: nextPaused }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to toggle pause mode.');
      }
      setGlobalSettings((prev) => ({
        ...prev,
        pauseAllExceptNewArrivals: nextPaused,
      }));
      if (Array.isArray(data?.collections)) {
        setCollections(data.collections);
        const nextDrafts = {};
        data.collections.forEach((collection) => {
          nextDrafts[collection.id] = normalizeRowForDraft(collection);
        });
        setDrafts(nextDrafts);
      }
      setMessage(data?.message || (nextPaused ? 'Paused all collections except New Arrivals.' : 'Safe mode disabled.'));
    } catch (err) {
      setError(err.message || 'Failed to toggle pause mode.');
    } finally {
      setBusyTogglingMasterPause(false);
    }
  }, [authedFetch]);

  const saveGlobalSettings = useCallback(async () => {
    setSavingGlobalSettings(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        settings: {
          pauseAllExceptNewArrivals: Boolean(globalSettings.pauseAllExceptNewArrivals),
          snapshotRetentionWeeks: Math.max(1, Number(globalSettings.snapshotRetentionWeeks || 1)),
          scoringWeights: {
            salesMomentum: Math.max(0, Number(globalSettings?.scoringWeights?.salesMomentum || 0)),
            ga4Demand: Math.max(0, Number(globalSettings?.scoringWeights?.ga4Demand || 0)),
            inventory: Math.max(0, Number(globalSettings?.scoringWeights?.inventory || 0)),
          },
          lookbacks: {
            salesDays: Math.max(1, Number(globalSettings?.lookbacks?.salesDays || 1)),
            ga4Days: Math.max(1, Number(globalSettings?.lookbacks?.ga4Days || 1)),
            seasonalityMode: String(globalSettings?.lookbacks?.seasonalityMode || 'all_time'),
            seasonalityWindowDays: Math.max(1, Number(globalSettings?.lookbacks?.seasonalityWindowDays || 1)),
          },
          defaultSchedule: {
            frequency: String(globalSettings?.defaultSchedule?.frequency || 'weekly'),
            intervalDays: Math.max(1, Number(globalSettings?.defaultSchedule?.intervalDays || 1)),
            dayOfWeek: String(globalSettings?.defaultSchedule?.dayOfWeek || 'MONDAY'),
            time: String(globalSettings?.defaultSchedule?.time || '03:00'),
            timeZone: String(globalSettings?.defaultSchedule?.timeZone || 'Asia/Kolkata'),
          },
        },
      };
      const response = await authedFetch('/api/v1/collection-automation/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to save global settings.');
      }
      const incoming = data?.settings || payload.settings;
      setGlobalSettings({
        pauseAllExceptNewArrivals: incoming.pauseAllExceptNewArrivals !== undefined ? Boolean(incoming.pauseAllExceptNewArrivals) : true,
        snapshotRetentionWeeks: Number(incoming.snapshotRetentionWeeks ?? 4) || 4,
        scoringWeights: {
          salesMomentum: Number(incoming?.scoringWeights?.salesMomentum ?? 55) || 55,
          ga4Demand: Number(incoming?.scoringWeights?.ga4Demand ?? 35) || 35,
          inventory: Number(incoming?.scoringWeights?.inventory ?? 10) || 10,
        },
        lookbacks: {
          salesDays: Number(incoming?.lookbacks?.salesDays ?? 90) || 90,
          ga4Days: Number(incoming?.lookbacks?.ga4Days ?? 90) || 90,
          seasonalityMode: String(incoming?.lookbacks?.seasonalityMode || 'all_time') || 'all_time',
          seasonalityWindowDays: Number(incoming?.lookbacks?.seasonalityWindowDays ?? 45) || 45,
        },
        defaultSchedule: {
          frequency: String(incoming?.defaultSchedule?.frequency || 'weekly'),
          intervalDays: Number(incoming?.defaultSchedule?.intervalDays ?? 7) || 7,
          dayOfWeek: String(incoming?.defaultSchedule?.dayOfWeek || 'MONDAY'),
          time: String(incoming?.defaultSchedule?.time || '03:00'),
          timeZone: String(incoming?.defaultSchedule?.timeZone || 'Asia/Kolkata'),
        },
      });
      setMessage('Global settings saved.');
      setShowGlobalSettings(false);
    } catch (err) {
      setError(err.message || 'Failed to save global settings.');
    } finally {
      setSavingGlobalSettings(false);
    }
  }, [authedFetch, globalSettings]);

  const openDiff = useCallback(async (collection) => {
    if (!collection?.id) return;
    setDiffLoading(true);
    setError('');
    setMessage('');
    setShowDiffModal(true);
    setDiffData({
      collectionTitle: String(collection.title || ''),
      runId: '',
      weekKey: '',
      summary: {},
      rows: [],
    });
    setDiffSort({ key: 'changeType', direction: 'asc' });

    try {
      const response = await authedFetch(`/api/v1/collection-automation/score/diff?collectionId=${encodeURIComponent(collection.id)}&limit=700`);
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load diff.');
      }
      setDiffData({
        collectionTitle: String(collection.title || ''),
        runId: String(data?.runId || ''),
        weekKey: String(data?.weekKey || ''),
        summary: data?.summary || {},
        rows: Array.isArray(data?.rows) ? data.rows : [],
      });
    } catch (err) {
      setError(err.message || 'Failed to load diff.');
    } finally {
      setDiffLoading(false);
    }
  }, [authedFetch]);

  const applyCollection = useCallback(async (collection) => {
    const collectionId = String(collection?.id || '').trim();
    if (!collectionId) return;
    const confirmed = window.confirm(`Apply latest scoring changes to Shopify for "${collection?.title || collectionId}"?`);
    if (!confirmed) return;

    setApplyingById((prev) => ({ ...prev, [collectionId]: true }));
    setError('');
    setMessage('');
    try {
      const response = await authedFetch('/api/v1/collection-automation/score/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          collectionId,
          execute: true,
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to apply changes.');
      }
      const added = Number(data?.applied?.added || 0);
      const removed = Number(data?.applied?.removed || 0);
      setMessage(`Applied ${collection?.title || collectionId}: +${added} / -${removed}.`);
      await loadCollections();
    } catch (err) {
      setError(err.message || 'Failed to apply changes.');
    } finally {
      setApplyingById((prev) => ({ ...prev, [collectionId]: false }));
    }
  }, [authedFetch, loadCollections]);

  const runCollectionAnalytic = useCallback(async (collection) => {
    const collectionId = String(collection?.id || '').trim();
    if (!collectionId) return;

    setScoringById((prev) => ({ ...prev, [collectionId]: true }));
    setError('');
    setMessage('');
    try {
      const response = await authedFetch('/api/v1/collection-automation/score/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          collectionIds: [collectionId],
          dryRun: true,
          preSyncProducts: false,
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to run analytics.');
      }
      const runId = String(data?.runId || '');
      setMessage(`Analytic completed for ${collection?.title || collectionId}${runId ? ` (Run: ${runId})` : ''}.`);
      await loadCollections();
      await openDiff(collection);
    } catch (err) {
      setError(err.message || 'Failed to run analytics.');
    } finally {
      setScoringById((prev) => ({ ...prev, [collectionId]: false }));
    }
  }, [authedFetch, loadCollections, openDiff]);

  const refreshCollectionSnapshot = useCallback(async (collection) => {
    const collectionId = String(collection?.id || '').trim();
    if (!collectionId) return;
    setSnapshotById((prev) => ({ ...prev, [collectionId]: true }));
    setError('');
    setMessage('');
    try {
      const response = await authedFetch('/api/v1/collection-automation/snapshot-membership', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ collectionIds: [collectionId] }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to refresh snapshot.');
      }
      setMessage(`Snapshot refreshed for ${collection?.title || collectionId}.`);
      await loadCollections();
    } catch (err) {
      setError(err.message || 'Failed to refresh snapshot.');
    } finally {
      setSnapshotById((prev) => ({ ...prev, [collectionId]: false }));
    }
  }, [authedFetch, loadCollections]);

  const applyAllEnabledCollections = useCallback(async () => {
    const confirmed = window.confirm('Apply latest scoring to all targeted + enabled manual collections in Shopify?');
    if (!confirmed) return;

    setBusyApplyAll(true);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch('/api/v1/collection-automation/score/apply-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ execute: true }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to apply all collections.');
      }
      const okCount = Number(data?.okCount || 0);
      const failCount = Number(data?.failCount || 0);
      const total = Number(data?.total || 0);
      if (failCount > 0) {
        const failedTitles = (Array.isArray(data?.results) ? data.results : [])
          .filter((r) => r?.ok === false)
          .slice(0, 5)
          .map((r) => String(r?.title || r?.id || '').trim())
          .filter(Boolean);
        const failedHint = failedTitles.length > 0 ? ` Failed: ${failedTitles.join(', ')}${failCount > failedTitles.length ? '...' : ''}.` : '';
        setError(`Apply-all finished with partial success. ${okCount}/${total} applied, ${failCount} failed.${failedHint}`);
      } else {
        setMessage(`Apply-all completed. ${okCount}/${total} collections applied successfully.`);
      }
      await loadCollections();
    } catch (err) {
      setError(err.message || 'Failed to apply all collections.');
    } finally {
      setBusyApplyAll(false);
    }
  }, [authedFetch, loadCollections]);

  const runAllReorderOnlyCollections = useCallback(async () => {
    const confirmed = window.confirm('Run Snapshot + Calculate + Apply for all targeted + enabled Reorder Only collections?');
    if (!confirmed) return;

    setBusyRunAllReorder(true);
    setError('');
    setMessage('');
    setBulkData({ message: '', total: 0, okCount: 0, failCount: 0, results: [] });
    try {
      const response = await authedFetch('/api/v1/collection-automation/reorder-only/run-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      const normalized = {
        message: String(data?.message || ''),
        total: Number(data?.total || 0),
        okCount: Number(data?.okCount || 0),
        failCount: Number(data?.failCount || 0),
        results: Array.isArray(data?.results) ? data.results : [],
      };
      if (!response.ok) {
        throw new Error(normalized.message || 'Failed to run reorder-only bulk.');
      }
      setBulkData(normalized);
      setShowBulkModal(true);
      if (normalized.failCount > 0) {
        setError(normalized.message || `Reorder-only run finished with ${normalized.failCount} failures.`);
      } else {
        setMessage(normalized.message || 'Reorder-only run completed.');
      }
      await loadCollections();
    } catch (err) {
      setError(err.message || 'Failed to run reorder-only bulk.');
    } finally {
      setBusyRunAllReorder(false);
    }
  }, [authedFetch, loadCollections]);

  const calculateAllReorderOnlyCollections = useCallback(async () => {
    const confirmed = window.confirm('Run Snapshot + Calculate for all targeted + enabled Reorder Only collections? This is testing-only and will not apply to Shopify.');
    if (!confirmed) return;

    setBusyCalcAllReorder(true);
    setError('');
    setMessage('');
    setBulkData({ message: '', total: 0, okCount: 0, failCount: 0, results: [] });
    try {
      const response = await authedFetch('/api/v1/collection-automation/reorder-only/calculate-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      const normalized = {
        message: String(data?.message || ''),
        total: Number(data?.total || 0),
        okCount: Number(data?.okCount || 0),
        failCount: Number(data?.failCount || 0),
        results: Array.isArray(data?.results) ? data.results : [],
      };
      if (!response.ok) {
        throw new Error(normalized.message || 'Failed to recalculate reorder-only collections.');
      }
      setBulkData(normalized);
      setShowBulkModal(true);
      if (normalized.failCount > 0) {
        setError(normalized.message || `Reorder-only recalculate finished with ${normalized.failCount} failures.`);
      } else {
        setMessage(normalized.message || 'Reorder-only recalculate completed.');
      }
      await loadCollections();
    } catch (err) {
      setError(err.message || 'Failed to recalculate reorder-only collections.');
    } finally {
      setBusyCalcAllReorder(false);
    }
  }, [authedFetch, loadCollections]);

  const openLastReorderOnlyRun = useCallback(async () => {
    setError('');
    setMessage('');
    setBulkData({ message: '', total: 0, okCount: 0, failCount: 0, results: [] });
    try {
      const response = await authedFetch('/api/v1/collection-automation/reorder-only/last-run');
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load last run.');
      }
      const run = data?.run || null;
      if (!run) {
        setMessage(data?.message || 'No reorder-only runs found yet.');
        return;
      }
      setBulkData({
        message: String(run?.message || ''),
        total: Number(run?.total || 0),
        okCount: Number(run?.okCount || 0),
        failCount: Number(run?.failCount || 0),
        results: Array.isArray(run?.results) ? run.results : [],
      });
      setShowBulkModal(true);
    } catch (err) {
      setError(err.message || 'Failed to load last run.');
    }
  }, [authedFetch]);

  const sortedDiffRows = useMemo(() => {
    const rows = Array.isArray(diffData?.rows) ? [...diffData.rows] : [];
    const key = String(diffSort?.key || 'changeType');
    const dir = diffSort?.direction === 'desc' ? -1 : 1;

    const numVal = (v) => (v === null || v === undefined || v === '' ? Number.POSITIVE_INFINITY : Number(v));
    const strVal = (v) => String(v || '').toLowerCase();

    rows.sort((a, b) => {
      if (key === 'oldPosition' || key === 'newPosition' || key === 'positionDelta') {
        const av = numVal(a?.[key]);
        const bv = numVal(b?.[key]);
        if (av === bv) return 0;
        return av > bv ? dir : -dir;
      }
      const av = strVal(a?.[key]);
      const bv = strVal(b?.[key]);
      if (av === bv) return 0;
      return av > bv ? dir : -dir;
    });
    return rows;
  }, [diffData?.rows, diffSort]);

  const computedDiffSummary = useMemo(() => {
    const summary = diffData?.summary || {};
    const rows = Array.isArray(diffData?.rows) ? diffData.rows : [];
    const hasCurrent = Number.isFinite(Number(summary.currentSize));
    const hasProjected = Number.isFinite(Number(summary.projectedSize));
    if (hasCurrent && hasProjected) return summary;

    const toBool = (v) => {
      const s = String(v ?? '').toLowerCase();
      return s === 'true' || s === '1';
    };
    const currentSize = rows.filter((r) => toBool(r.oldMember)).length;
    const projectedSize = rows.filter((r) => toBool(r.newMember)).length;
    return {
      ...summary,
      currentSize,
      projectedSize,
    };
  }, [diffData?.summary, diffData?.rows]);

  const handleDiffSort = useCallback((key) => {
    setDiffSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const sortLabel = useCallback((label, key) => {
    if (diffSort.key !== key) return label;
    return `${label} ${diffSort.direction === 'asc' ? '↑' : '↓'}`;
  }, [diffSort]);

  const copyDiffJson = useCallback(async () => {
    try {
      const payload = {
        collectionTitle: diffData?.collectionTitle || '',
        collectionId: diffData?.collectionId || '',
        runId: diffData?.runId || '',
        weekKey: diffData?.weekKey || '',
        summary: computedDiffSummary || {},
        rows: Array.isArray(diffData?.rows) ? diffData.rows : [],
      };
      const text = JSON.stringify(payload, null, 2);
      await navigator.clipboard.writeText(text);
      setMessage('Diff JSON copied to clipboard.');
    } catch (error) {
      setError(error?.message || 'Failed to copy diff JSON.');
    }
  }, [computedDiffSummary, diffData]);

  return (
    <div className="collections-automation-container">
      <div className="collections-automation-header">
        <div>
          <h1>Collection Automation</h1>
          <p>Choose target manual collections and configure weekly settings.</p>
        </div>
        <div className="collections-automation-actions">
          <button type="button" className="primary-btn" onClick={applyAllEnabledCollections} disabled={busyApplyAll || loading}>
            {busyApplyAll ? 'Applying All...' : 'Apply All Enabled'}
          </button>
          <button type="button" className="secondary-btn" onClick={calculateAllReorderOnlyCollections} disabled={busyCalcAllReorder || busyRunAllReorder || loading}>
            {busyCalcAllReorder ? 'Calculating Reorder...' : 'Recalculate All Reorder-Only'}
          </button>
          <button type="button" className="primary-btn" onClick={runAllReorderOnlyCollections} disabled={busyRunAllReorder || loading}>
            {busyRunAllReorder ? 'Running Reorder...' : 'Run All Reorder-Only'}
          </button>
          <button type="button" className="secondary-btn" onClick={openLastReorderOnlyRun} disabled={busyCalcAllReorder || busyRunAllReorder || loading}>
            Last Reorder Results
          </button>
          <button type="button" className="secondary-btn" onClick={runSyncCollections} disabled={busySync || loading}>
            {busySync ? 'Syncing...' : 'Sync Collections'}
          </button>
          <button type="button" className="primary-btn" onClick={runSnapshot} disabled={busySnapshot || loading}>
            {busySnapshot ? 'Snapshotting...' : 'Snapshot Membership'}
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={() => setShowGlobalSettings(true)}
            title="Global settings"
            aria-label="Global settings"
          >
            ⚙
          </button>
        </div>
      </div>

      <div className={`master-pause-card ${globalSettings.pauseAllExceptNewArrivals ? 'is-paused' : 'is-active'}`}>
        <div className="master-pause-left">
          <div className="master-pause-badge">
            <span className="master-pause-dot" />
            <span className="master-pause-status-text">
              {globalSettings.pauseAllExceptNewArrivals
                ? 'PAUSED (EXCEPT NEW ARRIVALS)'
                : 'ALL AUTOMATIONS ACTIVE'}
            </span>
          </div>
          <div className="master-pause-info">
            <h3>
              {globalSettings.pauseAllExceptNewArrivals
                ? 'Safe Mode: Only "New Arrivals" is active'
                : 'Normal Mode: Enabled collections run on schedule'}
            </h3>
            <p>
              {globalSettings.pauseAllExceptNewArrivals
                ? 'All other 51 collections & weekly automated runs are paused. Fabric Stock Rules continue running daily at 00:02 AM IST.'
                : 'Weekly Monday scheduled jobs will process all collections marked as "Enabled".'}
            </p>
          </div>
        </div>
        <div className="master-pause-right">
          <label className="master-toggle-switch" title="Toggle Pause Everything Except New Arrivals">
            <input
              type="checkbox"
              checked={Boolean(globalSettings.pauseAllExceptNewArrivals)}
              onChange={handleTogglePauseAllExceptNewArrivals}
              disabled={busyTogglingMasterPause || loading}
            />
            <span className="master-toggle-slider" />
          </label>
          <div className="master-toggle-label">
            <strong>{globalSettings.pauseAllExceptNewArrivals ? 'Pause Mode: ON' : 'Pause Mode: OFF'}</strong>
            <span>{busyTogglingMasterPause ? 'Updating...' : (globalSettings.pauseAllExceptNewArrivals ? 'Click to resume all' : 'Click to pause all')}</span>
          </div>
        </div>
      </div>

      <div className="collections-automation-filters">
        <input
          type="text"
          placeholder="Search title / handle / ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label>
          <input type="checkbox" checked={manualOnly} onChange={(e) => setManualOnly(e.target.checked)} />
          Manual only
        </label>
        <label>
          <input type="checkbox" checked={targetedOnly} onChange={(e) => setTargetedOnly(e.target.checked)} />
          Targeted only
        </label>
        <label>
          <input type="checkbox" checked={reorderOnlyOnly} onChange={(e) => setReorderOnlyOnly(e.target.checked)} />
          Reorder only
        </label>
        <button
          type="button"
          className="secondary-btn"
          onClick={() => {
            setError('');
            setMessage('');
            loadCollections();
          }}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {error ? <p className="collections-automation-error">{error}</p> : null}
      {message ? <p className="collections-automation-message">{message}</p> : null}

      <div className="collections-automation-table-wrap">
        {loading ? <div className="collections-automation-loading">Loading collections...</div> : null}
        <table className={`collections-automation-table ${loading ? 'is-loading' : ''}`}>
          <thead>
            <tr>
              <th>Collection</th>
              <th>Target</th>
              <th>Enabled</th>
              <th>Manual</th>
              <th>Count</th>
              <th>Reorder Only</th>
              <th>Max Add</th>
              <th>Max Remove</th>
              <th>Min Size</th>
              <th>Max Size</th>
              <th>Cooldown X</th>
              <th>Cooldown Scope</th>
              <th>Run Mode</th>
              <th>Allowed Product Types</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCollections.map((collection) => {
              const draft = drafts[collection.id] || normalizeRowForDraft(collection);
              const settings = draft.settings || defaultRowSettings;
              const busy = savingById[collection.id] === true;
              const applying = applyingById[collection.id] === true;
              const scoring = scoringById[collection.id] === true;
              const snapshotting = snapshotById[collection.id] === true;
              return (
                <React.Fragment key={collection.id}>
                  <tr>
                    <td>
                      <div className="collection-cell-title">{collection.title || 'Untitled'}</div>
                      <div className="collection-cell-sub">{collection.handle ? `/${collection.handle}` : 'No handle'}</div>
                      <div className="collection-cell-sub muted">{collection.legacyCollectionId || collection.collectionGid || collection.id}</div>
                    </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={draft.isTargeted === true}
                      onChange={(e) => updateDraft(collection.id, (current) => ({ ...current, isTargeted: e.target.checked }))}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={settings.enabled === true}
                      onChange={(e) => setSettingField(collection.id, 'enabled', e.target.checked)}
                    />
                  </td>
                  <td>{collection.isManual ? 'Yes' : 'No'}</td>
                  <td>{resolveCount(collection) != null ? resolveCount(collection) : '—'}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={settings.reorderOnly === true}
                      onChange={(e) => setSettingField(collection.id, 'reorderOnly', e.target.checked)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={settings.maxAddPerRun ?? 0}
                      onChange={(e) => setSettingField(collection.id, 'maxAddPerRun', Number(e.target.value || 0))}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={settings.maxRemovePerRun ?? 0}
                      onChange={(e) => setSettingField(collection.id, 'maxRemovePerRun', Number(e.target.value || 0))}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={settings.minCollectionSize ?? 0}
                      onChange={(e) => setSettingField(collection.id, 'minCollectionSize', Number(e.target.value || 0))}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={settings.maxCollectionSize ?? 0}
                      onChange={(e) => setSettingField(collection.id, 'maxCollectionSize', Number(e.target.value || 0))}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={settings.cooldownTopX ?? 0}
                      onChange={(e) => setSettingField(collection.id, 'cooldownTopX', Number(e.target.value || 0))}
                    />
                  </td>
                  <td>
                    <select
                      value={settings.cooldownScope || 'per_collection'}
                      onChange={(e) => setSettingField(collection.id, 'cooldownScope', e.target.value)}
                    >
                      <option value="per_collection">Per collection</option>
                      <option value="global">Global</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={settings.runMode || 'dry_run'}
                      onChange={(e) => setSettingField(collection.id, 'runMode', e.target.value)}
                    >
                      <option value="dry_run">Dry run</option>
                      <option value="auto_apply">Auto apply</option>
                    </select>
                  </td>
                  <td rowSpan={2}>
                    <select
                      multiple
                      value={Array.isArray(settings.allowedProductTypes) ? settings.allowedProductTypes : []}
                      onChange={(e) => {
                        const values = Array.from(e.target.selectedOptions || []).map((opt) => opt.value);
                        setAllowedProductTypes(collection.id, values);
                      }}
                      title="Select one or more product types"
                    >
                      {productTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    <div className="collection-cell-sub">
                      {(settings.allowedProductTypes || []).length} selected
                    </div>
                  </td>
                  <td rowSpan={2}>
                    <div className="ca-actions-cell">
                      <div className="ca-actions-row">
                        <button type="button" className="primary-btn" disabled={busy} onClick={() => saveRow(collection.id)}>
                          {busy ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" className="secondary-btn" onClick={() => openDiff(collection)}>
                          Diff
                        </button>
                      </div>
                      <div className="ca-actions-row">
                        <button type="button" className="secondary-btn" disabled={scoring} onClick={() => runCollectionAnalytic(collection)}>
                          {scoring ? 'Running...' : 'Calculate'}
                        </button>
                        <button type="button" className="secondary-btn" disabled={applying} onClick={() => applyCollection(collection)}>
                          {applying ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                      <div className="ca-actions-row">
                        <button
                          type="button"
                          className="secondary-btn"
                          disabled={snapshotting}
                          onClick={() => refreshCollectionSnapshot(collection)}
                        >
                          {snapshotting ? 'Refreshing...' : 'Refresh Snapshot'}
                        </button>
                      </div>
                    </div>
                  </td>
                  </tr>
                  <tr className="ca-status-row">
                    <td colSpan="13">
                      {(() => {
                        const state = collection?.automationState || {};
                        const lastCalculated = state?.lastCalculated || {};
                        const lastApplied = state?.lastApplied || {};
                        const lastApplyAtMs = toMillisSafe(lastApplied?.at);
                        const lastOkApply = lastApplied?.ok === true;
                        const hasLegacyApplyMarker = Boolean(
                          lastApplied?.runId
                          || lastApplied?.weekKey
                          || (lastApplied?.summary && typeof lastApplied.summary === 'object')
                        );
                        const stale = lastOkApply && lastApplyAtMs && (Date.now() - lastApplyAtMs) > (APPLY_ALERT_STALE_DAYS * DAY_MS);
                        const failed = lastApplied?.ok === false;
                        const neverApplied = draft.isTargeted === true && settings.enabled === true && !lastApplyAtMs && !hasLegacyApplyMarker;
                        const showAlert = failed || stale || neverApplied;
                        const modeLabel = settings.reorderOnly === true ? 'Reorder only' : 'Full automation';
                        const applySummary = lastApplied?.summary && typeof lastApplied.summary === 'object' ? lastApplied.summary : null;
                        const changeSummary = applySummary
                          ? `+${Number(applySummary.added || 0)} / -${Number(applySummary.removed || 0)} / moved ${Number(applySummary.reordered || 0)} / size ${Number(applySummary.afterSize || 0)}`
                          : '—';
                        const alertText = failed
                          ? (String(lastApplied?.message || 'Last apply failed.'))
                          : (neverApplied ? 'Never applied yet.' : (stale ? `Last successful apply is older than ${APPLY_ALERT_STALE_DAYS} days.` : ''));
                        const calcBadge = lastCalculated?.ok === true ? 'OK' : (lastCalculated?.ok === false ? 'Fail' : '—');
                        const applyBadge = lastApplied?.ok === true ? 'OK' : (lastApplied?.ok === false ? 'Fail' : '—');
                        return (
                          <div className={`ca-status-bar ${showAlert ? 'has-alert' : ''}`} title={showAlert ? alertText : ''}>
                            <div className="ca-status-item">
                              <span className="ca-status-label">Mode</span>
                              <span className="ca-status-value">{modeLabel}</span>
                            </div>
                            <div className="ca-status-item">
                              <span className="ca-status-label">Last Calculated</span>
                              <span className={`ca-status-pill ${calcBadge === 'OK' ? 'ok' : (calcBadge === 'Fail' ? 'fail' : '')}`}>{calcBadge}</span>
                              <span className="ca-status-value">{formatRelative(lastCalculated?.at)} <span className="muted">({formatDateTime(lastCalculated?.at)})</span></span>
                            </div>
                            <div className="ca-status-item">
                              <span className="ca-status-label">Last Applied</span>
                              <span className={`ca-status-pill ${applyBadge === 'OK' ? 'ok' : (applyBadge === 'Fail' ? 'fail' : '')}`}>{applyBadge}</span>
                              <span className="ca-status-value">{formatRelative(lastApplied?.at)} <span className="muted">({formatDateTime(lastApplied?.at)})</span></span>
                            </div>
                            <div className="ca-status-item">
                              <span className="ca-status-label">Last Change</span>
                              <span className="ca-status-value">{changeSummary}</span>
                            </div>
                            {showAlert ? (
                              <div className="ca-status-item ca-status-alert">
                                <span className="ca-status-pill warn">Alert</span>
                                <span className="ca-status-value">{alertText}</span>
                              </div>
                            ) : null}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
            {!loading && filteredCollections.length === 0 ? (
              <tr>
                <td colSpan="15" className="collections-empty">No collections match current filters.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {showGlobalSettings ? (
        <div className="ca-modal-backdrop" onClick={() => setShowGlobalSettings(false)}>
          <div className="ca-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Global Settings</h3>
            <p className="collection-cell-sub">Configure defaults for collection automation.</p>
            <div className="ca-modal-field">
              <label htmlFor="snapshotRetentionWeeks">Keep Last N Snapshots</label>
              <input
                id="snapshotRetentionWeeks"
                type="number"
                min="1"
                value={globalSettings.snapshotRetentionWeeks}
                onChange={(e) => setGlobalSettings((prev) => ({
                  ...prev,
                  snapshotRetentionWeeks: Math.max(1, Number(e.target.value || 1)),
                }))}
              />
              <small>Older snapshot history will be pruned after each snapshot run.</small>
            </div>

            <div className="ca-modal-grid">
              <div className="ca-modal-field">
                <label htmlFor="salesMomentumWeight">Sales Weight (%)</label>
                <input
                  id="salesMomentumWeight"
                  type="number"
                  min="0"
                  value={globalSettings?.scoringWeights?.salesMomentum ?? 55}
                  onChange={(e) => setGlobalSettings((prev) => ({
                    ...prev,
                    scoringWeights: {
                      ...(prev.scoringWeights || {}),
                      salesMomentum: Math.max(0, Number(e.target.value || 0)),
                    },
                  }))}
                />
              </div>
              <div className="ca-modal-field">
                <label htmlFor="ga4DemandWeight">GA4 Demand Weight (%)</label>
                <input
                  id="ga4DemandWeight"
                  type="number"
                  min="0"
                  value={globalSettings?.scoringWeights?.ga4Demand ?? 35}
                  onChange={(e) => setGlobalSettings((prev) => ({
                    ...prev,
                    scoringWeights: {
                      ...(prev.scoringWeights || {}),
                      ga4Demand: Math.max(0, Number(e.target.value || 0)),
                    },
                  }))}
                />
              </div>
              <div className="ca-modal-field">
                <label htmlFor="inventoryWeight">Inventory Weight (%)</label>
                <input
                  id="inventoryWeight"
                  type="number"
                  min="0"
                  value={globalSettings?.scoringWeights?.inventory ?? 10}
                  onChange={(e) => setGlobalSettings((prev) => ({
                    ...prev,
                    scoringWeights: {
                      ...(prev.scoringWeights || {}),
                      inventory: Math.max(0, Number(e.target.value || 0)),
                    },
                  }))}
                />
              </div>
            </div>
            <small>Default split is 55 / 35 / 10. You can change this anytime.</small>

            <div className="ca-modal-grid">
              <div className="ca-modal-field">
                <label htmlFor="salesLookbackDays">Sales Lookback (Days)</label>
                <input
                  id="salesLookbackDays"
                  type="number"
                  min="1"
                  value={globalSettings?.lookbacks?.salesDays ?? 90}
                  onChange={(e) => setGlobalSettings((prev) => ({
                    ...prev,
                    lookbacks: {
                      ...(prev.lookbacks || {}),
                      salesDays: Math.max(1, Number(e.target.value || 1)),
                    },
                  }))}
                />
              </div>
              <div className="ca-modal-field">
                <label htmlFor="ga4LookbackDays">GA4 Lookback (Days)</label>
                <input
                  id="ga4LookbackDays"
                  type="number"
                  min="1"
                  value={globalSettings?.lookbacks?.ga4Days ?? 90}
                  onChange={(e) => setGlobalSettings((prev) => ({
                    ...prev,
                    lookbacks: {
                      ...(prev.lookbacks || {}),
                      ga4Days: Math.max(1, Number(e.target.value || 1)),
                    },
                  }))}
                />
              </div>
              <div className="ca-modal-field">
                <label htmlFor="seasonalityMode">Seasonality</label>
                <select
                  id="seasonalityMode"
                  value={globalSettings?.lookbacks?.seasonalityMode || 'all_time'}
                  onChange={(e) => setGlobalSettings((prev) => ({
                    ...prev,
                    lookbacks: {
                      ...(prev.lookbacks || {}),
                      seasonalityMode: e.target.value || 'all_time',
                    },
                  }))}
                >
                  <option value="all_time">All available data</option>
                  <option value="current_period_multi_year">Current period (multi-year)</option>
                </select>
              </div>
              <div className="ca-modal-field">
                <label htmlFor="seasonalityWindowDays">Seasonality Window (Days)</label>
                <input
                  id="seasonalityWindowDays"
                  type="number"
                  min="1"
                  value={globalSettings?.lookbacks?.seasonalityWindowDays ?? 45}
                  onChange={(e) => setGlobalSettings((prev) => ({
                    ...prev,
                    lookbacks: {
                      ...(prev.lookbacks || {}),
                      seasonalityWindowDays: Math.max(1, Number(e.target.value || 1)),
                    },
                  }))}
                />
              </div>
            </div>



            <div className="ca-modal-actions">
              <button type="button" className="secondary-btn" onClick={() => setShowGlobalSettings(false)}>Cancel</button>
              <button type="button" className="primary-btn" onClick={saveGlobalSettings} disabled={savingGlobalSettings}>
                {savingGlobalSettings ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showBulkModal ? (
        <div className="ca-modal-backdrop" onClick={() => setShowBulkModal(false)}>
          <div className="ca-modal ca-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Reorder-Only Run Results</h3>
            <div className="ca-diff-summary">
              <span>Total: {Number(bulkData.total || 0)}</span>
              <span>Success: {Number(bulkData.okCount || 0)}</span>
              <span>Failed: {Number(bulkData.failCount || 0)}</span>
            </div>
            {bulkData.message ? <p className="collection-cell-sub">{bulkData.message}</p> : null}
            <div className="ca-diff-table-wrap">
              <table className="collections-automation-table ca-diff-table">
                <thead>
                  <tr>
                    <th>Collection</th>
                    <th>Status</th>
                    <th>Snapshot Rows</th>
                    <th>Run Id</th>
                    <th>Moved</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(bulkData.results) ? bulkData.results : []).map((row) => (
                    <tr key={String(row?.id || '')}>
                      <td>{String(row?.title || row?.id || '')}</td>
                      <td>{row?.ok ? 'ok' : 'failed'}</td>
                      <td>{row?.snapshot?.ok ? Number(row?.snapshot?.latestRows || 0) : '—'}</td>
                      <td>{row?.calculate?.ok ? String(row?.calculate?.runId || '') : '—'}</td>
                      <td>{row?.apply?.ok ? Number(row?.apply?.reordered || 0) : '—'}</td>
                      <td>{row?.ok ? '' : String(row?.message || '')}</td>
                    </tr>
                  ))}
                  {(Array.isArray(bulkData.results) ? bulkData.results : []).length === 0 ? (
                    <tr>
                      <td colSpan="6" className="collections-empty">No results yet.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="ca-modal-actions">
              <button type="button" className="secondary-btn" onClick={() => setShowBulkModal(false)}>Close</button>
            </div>
          </div>
        </div>
      ) : null}

      {showDiffModal ? (
        <div className="ca-modal-backdrop" onClick={() => setShowDiffModal(false)}>
          <div className="ca-modal ca-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Collection Diff: {diffData.collectionTitle || 'Collection'}</h3>
            <p className="collection-cell-sub">
              {diffData.weekKey ? `Week ${diffData.weekKey}` : 'Latest run'} {diffData.runId ? `| Run ${diffData.runId}` : ''}
            </p>

            <div className="ca-diff-summary">
              <span>Current Size: {Number(computedDiffSummary?.currentSize || 0)}</span>
              <span>Added: {Number(computedDiffSummary?.added || 0)}</span>
              <span>Removed: {Number(computedDiffSummary?.removed || 0)}</span>
              <span>Projected Size: {Number(computedDiffSummary?.projectedSize || 0)}</span>
              <span>Moved: {Number(computedDiffSummary?.positionChanged || 0)}</span>
              <span>Unchanged: {Number(computedDiffSummary?.unchanged || 0)}</span>
            </div>

            {diffLoading ? (
              <div className="collections-automation-loading">Loading diff...</div>
            ) : (
              <div className="ca-diff-table-wrap">
                <table className="collections-automation-table ca-diff-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>
                        <button type="button" className="ca-sort-btn" onClick={() => handleDiffSort('changeType')}>
                          {sortLabel('Change', 'changeType')}
                        </button>
                      </th>
                      <th>
                        <button type="button" className="ca-sort-btn" onClick={() => handleDiffSort('oldPosition')}>
                          {sortLabel('Old Pos', 'oldPosition')}
                        </button>
                      </th>
                      <th>
                        <button type="button" className="ca-sort-btn" onClick={() => handleDiffSort('newPosition')}>
                          {sortLabel('New Pos', 'newPosition')}
                        </button>
                      </th>
                      <th>
                        <button type="button" className="ca-sort-btn" onClick={() => handleDiffSort('positionDelta')}>
                          {sortLabel('Delta', 'positionDelta')}
                        </button>
                      </th>
                      <th>
                        <button type="button" className="ca-sort-btn" onClick={() => handleDiffSort('action')}>
                          {sortLabel('Action', 'action')}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDiffRows.map((row, idx) => (
                      <tr key={`${row.sku || 'sku'}-${idx}`}>
                        <td>{row.sku || '—'}</td>
                        <td>{row.changeType || 'unchanged'}</td>
                        <td>{row.oldPosition ?? '—'}</td>
                        <td>{row.newPosition ?? '—'}</td>
                        <td>{row.positionDelta ?? '—'}</td>
                        <td>{row.action || 'keep'}</td>
                      </tr>
                    ))}
                    {(!diffData.rows || diffData.rows.length === 0) ? (
                      <tr>
                        <td colSpan="6" className="collections-empty">No diff rows found for this collection yet.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}

            <div className="ca-modal-actions">
              <button type="button" className="secondary-btn" onClick={copyDiffJson}>Copy JSON</button>
              <button type="button" className="secondary-btn" onClick={() => setShowDiffModal(false)}>Close</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CollectionAutomationPage;
